import React, { useEffect, useState } from "react";
import {
  Users, UserPlus, Briefcase, CalendarClock, CheckCircle2, FileWarning,
  Plane, Wallet, TrendingUp, AlertTriangle, Stethoscope, FileText,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CHART_COLORS = ["#0088FF", "#0A192F", "#10B981", "#F59E0B", "#6366F1", "#00C3FF", "#EC4899", "#14B8A6"];

const KPI_DEFS = [
  { key: "total_candidates", label: "Total Candidates", icon: Users, color: "#0088FF" },
  { key: "new_candidates", label: "New Candidates", icon: UserPlus, color: "#00C3FF" },
  { key: "active_applications", label: "Active Applications", icon: Briefcase, color: "#6366F1" },
  { key: "interviews", label: "Interviews", icon: CalendarClock, color: "#8B5CF6" },
  { key: "selected", label: "Selected", icon: CheckCircle2, color: "#10B981" },
  { key: "medical_pending", label: "Medical Pending", icon: Stethoscope, color: "#14B8A6" },
  { key: "pending_documents", label: "Pending Documents", icon: FileText, color: "#F59E0B" },
  { key: "expiring_documents", label: "Expiring Documents", icon: FileWarning, color: "#EF4444" },
  { key: "deployed", label: "Deployed", icon: Plane, color: "#059669" },
  { key: "overdue_tasks", label: "Overdue Tasks", icon: AlertTriangle, color: "#DC2626" },
];

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 p-5 ${className}`}>{children}</div>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const { user } = useAuth();
  const isFinance = ["super_admin", "admin", "finance"].includes(user?.role);

  useEffect(() => {
    client.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    client.get("/dashboard/charts").then((r) => setCharts(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-400">Loading dashboard...</div>;
  const k = stats.kpis;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#0A192F]">Operations Dashboard</h1>
        <p className="text-sm text-slate-400">Live data across the recruitment pipeline</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {KPI_DEFS.map((d) => (
          <div key={d.key} data-testid={`kpi-${d.key}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover-lift">
            <div className="flex items-center justify-between">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${d.color}1a`, color: d.color }}>
                <d.icon className="h-5 w-5" />
              </div>
            </div>
            <div className="font-display text-3xl font-bold text-[#0A192F] mt-3">{k[d.key] ?? 0}</div>
            <div className="text-xs text-slate-500 mt-0.5">{d.label}</div>
          </div>
        ))}
      </div>

      {isFinance && stats.financial && Object.keys(stats.financial).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { l: "Revenue", v: stats.financial.revenue, c: "#10B981" },
            { l: "Expenses", v: stats.financial.expenses, c: "#EF4444" },
            { l: "Commission", v: stats.financial.commission, c: "#F59E0B" },
            { l: "Outstanding", v: stats.financial.outstanding, c: "#6366F1" },
            { l: "Margin", v: stats.financial.margin, c: "#0088FF" },
          ].map((f) => (
            <div key={f.l} data-testid={`fin-${f.l.toLowerCase()}`} className="brand-gradient-navy rounded-2xl p-5 text-white relative overflow-hidden">
              <div className="absolute inset-0 brand-dotgrid opacity-30" />
              <div className="relative">
                <div className="flex items-center gap-1.5 text-xs text-slate-300"><Wallet className="h-3.5 w-3.5" /> {f.l}</div>
                <div className="font-display text-xl font-bold mt-2" style={{ color: f.c === "#0088FF" ? "#fff" : f.c }}>
                  ₹{(f.v || 0).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {charts && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-display font-semibold text-[#0A192F] mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#0088FF]" /> Recruitment Funnel</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={charts.recruitment_funnel} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0088FF" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <h3 className="font-display font-semibold text-[#0A192F] mb-4">Candidates by Country</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={charts.candidates_by_country} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                  {charts.candidates_by_country.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <h3 className="font-display font-semibold text-[#0A192F] mb-4">Document Status</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts.document_status}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <h3 className="font-display font-semibold text-[#0A192F] mb-4">Candidates by Agency</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts.candidates_by_agency}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366F1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
    </div>
  );
}
